import type { DiffHunk, DiffLine, DiffLineType } from '@/features/moderation/api';
import { useT } from '@/i18n/LocaleProvider';

export type { DiffHunk, DiffLine, DiffLineType };

type UnifiedDiffViewProps = {
  hunks: DiffHunk[];
};

export function UnifiedDiffView({ hunks }: UnifiedDiffViewProps) {
  const t = useT();

  if (hunks.length === 0) {
    return (
      <div className="unified-diff" data-testid="proposal-diff">
        <div className="empty-note">{t('diff_no_changes')}</div>
      </div>
    );
  }

  return (
    <div className="unified-diff" data-testid="proposal-diff">
      {hunks.map((hunk, hunkIndex) => (
        <section className="diff-hunk" key={hunkKey(hunk, hunkIndex)}>
          <div className="diff-hunk-header">{hunkHeader(hunk)}</div>
          <div className="diff-lines">
            {hunk.lines.map((line, lineIndex) => (
              <DiffLineRow
                hunkIndex={hunkIndex}
                line={line}
                lineIndex={lineIndex}
                key={lineKey(line, hunkIndex, lineIndex)}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function DiffLineRow({
  hunkIndex,
  line,
  lineIndex,
}: {
  hunkIndex: number;
  line: DiffLine;
  lineIndex: number;
}) {
  return (
    <div
      className={`diff-line is-${line.type}`}
      data-testid={`diff-line-${hunkIndex}-${lineIndex}`}
    >
      <span className="diff-line-number">{line.old_line ?? ''}</span>
      <span className="diff-line-number">{line.new_line ?? ''}</span>
      <span className="diff-line-prefix">{linePrefix(line.type)}</span>
      <code>{line.content || ' '}</code>
    </div>
  );
}

function hunkHeader(hunk: DiffHunk): string {
  return `@@ -${hunk.old_start},${hunk.old_lines} +${hunk.new_start},${hunk.new_lines} @@`;
}

function hunkKey(hunk: DiffHunk, index: number): string {
  return `${hunk.old_start}:${hunk.new_start}:${index}`;
}

function lineKey(line: DiffLine, hunkIndex: number, lineIndex: number): string {
  return `${hunkIndex}:${lineIndex}:${line.type}:${line.old_line ?? ''}:${line.new_line ?? ''}`;
}

function linePrefix(type: DiffLineType): string {
  switch (type) {
    case 'delete':
      return '-';
    case 'insert':
      return '+';
    case 'equal':
      return ' ';
  }
}
